import React, { useEffect, useMemo, useState } from 'react';

import { usePermissions } from '../../context/PermissionContext';
import useLabOrderStore from '../../stores/labOrderStore';
import useDepartmentStore from '../../stores/departmentStore';
import { filterProvidersByDept } from './departmentResolution';
import { fetchStaffList } from '../../services/api/staffApi';
import { selectLabTechnicians } from '../../utils/labTechnicianRoster';
import { STAFF_ROLES } from '../../constants/staff';
import LabReportActions from '../lab/LabReportActions';

const memberId = (m) => m.member_id || m.id;
const memberName = (m) =>
  m.name || [m.first_name, m.last_name].filter(Boolean).join(' ').trim() || memberId(m);

/**
 * LabAssignPanel — the receptionist's "assign a technician" surface for the
 * labs advised on THIS visit (the same-day handoff). The doctor advises a test
 * (it lands `advised`, in no queue); reception picks a technician here and the
 * order enters that technician's queue. Assign controls are gated on
 * ASSIGN_LAB_ORDER; without it the panel is read-only (e.g. a technician
 * glancing at the visit's labs).
 */
export default function LabAssignPanel({ visit, readOnly = false }) {
  const { hasPermission } = usePermissions();
  const canAssign = hasPermission('ASSIGN_LAB_ORDER') && !readOnly;
  const { byVisit, assign, fetchForVisit, busyId } = useLabOrderStore();

  const [techs, setTechs] = useState([]);
  const [pick, setPick] = useState({}); // lab_order_id -> technician member_id
  const [msg, setMsg] = useState(null);

  const visitId = visit?.visit_id;
  const orders = byVisit[visitId] || [];

  // A2 completion — dept-scope the technician picker to the VISIT'S stamped
  // department of care (frozen at registration), the SAME rule as registration /
  // the procedure assignee picker. Anchor = visit.department_id; falsy → no
  // filter (org with 0 depts, or a no/emergency-dept visit). Single source:
  // shared `filterProvidersByDept` (keeps no-dept techs org-wide, excludes
  // other-dept, fail-opens on un-hydrated edges). Display-only; server re-validates.
  const deptsByMember = useDepartmentStore((s) => s.deptsByMember);
  const hydrateAllMemberDepts = useDepartmentStore((s) => s.hydrateAllMemberDepts);
  const visitDepartmentId = visit?.department_id || null;

  useEffect(() => {
    if (visitId) fetchForVisit(visitId);
  }, [fetchForVisit, visitId]);

  useEffect(() => {
    if (!canAssign) return;
    // LAB-ASSIGN/T3 — single source of truth for "what counts as a lab
    // technician" (shared with OrderLabDialog/RegisterVisitForm). The dept
    // narrowing below (`filterProvidersByDept`) still applies AFTER this,
    // unchanged.
    fetchStaffList().then((r) => {
      if (r.success) setTechs(selectLabTechnicians(r));
    });
  }, [canAssign]);

  useEffect(() => {
    if (canAssign && visitDepartmentId) hydrateAllMemberDepts?.();
  }, [canAssign, visitDepartmentId, hydrateAllMemberDepts]);

  const scopedTechs = useMemo(
    () => filterProvidersByDept(techs, visitDepartmentId, deptsByMember),
    [techs, visitDepartmentId, deptsByMember],
  );

  const doAssign = async (order) => {
    const techId = pick[order.lab_order_id];
    if (!techId) {
      setMsg({ k: 'err', t: 'Pick a technician first.' });
      return;
    }
    setMsg(null);
    const r = await assign(order.lab_order_id, {
      new_assignee_member_id: techId,
      new_assignee_role: STAFF_ROLES.LAB_TECHNICIAN,
    });
    if (r.success) {
      await fetchForVisit(visitId);
      setMsg({ k: 'ok', t: 'Technician assigned.' });
    } else {
      setMsg({ k: 'err', t: r.error || 'Failed to assign technician.' });
    }
  };

  const fieldCls =
    'rounded-lg px-2.5 py-2 text-sm border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100';

  return (
    <div className="rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-3">
      <div className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-2">🧪 Lab tests for this visit</div>

      {orders.length === 0 ? (
        <div className="text-sm text-slate-400 dark:text-slate-500 italic">No lab tests advised on this visit.</div>
      ) : (
        <div className="space-y-2">
          {orders.map((o) => {
            const unassigned = o.status === 'advised' && !o.assigned_to?.member_id;
            return (
              <div
                key={o.lab_order_id}
                className="flex flex-wrap items-center gap-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-2"
              >
                <span className="font-semibold text-slate-800 dark:text-slate-100">{o.test_name}</span>
                <span className="text-xs rounded-full px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                  {o.status}
                </span>
                {/* L1 — display-only IP/credit badges (solid #000 per thermal/
                    day-mode contrast rule). billing_mode/admission_id are
                    additive fields on the order; absent on every pre-L1 /
                    OPD order, so this renders nothing for them. The existing
                    "Collect ₹X" control below (LabReportActions) still shows
                    for an unpaid credit order — it IS the Day-1 settle path
                    (L1 AC8) — this badge never hides or replaces it. */}
                {o.admission_id && (
                  <span className="text-xs font-bold rounded-full px-2 py-0.5 bg-white dark:bg-slate-800 border border-slate-400 dark:border-slate-500 text-slate-800 dark:text-slate-100">
                    IP lab
                  </span>
                )}
                {o.billing_mode === 'credit' && !o.is_paid && (
                  <span className="text-xs font-bold rounded-full px-2 py-0.5 bg-black text-white">
                    CREDIT — NOT PAID
                  </span>
                )}
                {o.assigned_to?.member_id && (
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    🔬 {o.assigned_to.name || o.assigned_to.member_id}
                  </span>
                )}

                <div className="flex items-center gap-2 ml-auto">
                  {canAssign && unassigned && (
                    <>
                      <select
                        className={`${fieldCls} min-w-[150px]`}
                        value={pick[o.lab_order_id] || ''}
                        onChange={(e) => setPick((p) => ({ ...p, [o.lab_order_id]: e.target.value }))}
                      >
                        <option value="">Assign technician…</option>
                        {scopedTechs.map((m) => (
                          <option key={memberId(m)} value={memberId(m)}>
                            🔬 {memberName(m)}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => doAssign(o)}
                        disabled={busyId === o.lab_order_id || !pick[o.lab_order_id]}
                        className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
                      >
                        {busyId === o.lab_order_id ? 'Assigning…' : 'Assign'}
                      </button>
                    </>
                  )}
                  <LabReportActions order={o} reportOnly={readOnly} onChanged={() => fetchForVisit(visitId)} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {canAssign && techs.length === 0 && orders.some((o) => o.status === 'advised') && (
        <div className="text-xs text-amber-600 dark:text-amber-400 mt-2">
          No lab technicians on staff yet — add one under Manage Staff to assign these tests.
        </div>
      )}
      {msg && (
        <div className={`text-xs mt-2 ${msg.k === 'ok' ? 'text-teal-600 dark:text-teal-400' : 'text-rose-600 dark:text-rose-400'}`}>
          {msg.t}
        </div>
      )}
    </div>
  );
}
